<template>
    <v-dialog v-model="dialog" max-width="700px" persistent scrollable>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div class="booking-dialog-title">
                        <v-icon class="mr-2" color="white" size="20">mdi-email-send-outline</v-icon>
                        Send Invitee Login Details
                    </div>
                    <v-btn icon dark @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <v-card-text class="pa-6">
                <div class="booking-section mb-4">
                    <div class="booking-section-header mb-1">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-format-title</v-icon>
                        SUBJECT
                    </div>
                    <v-text-field v-model="subject" outlined dense placeholder="Enter email subject"
                        hide-details></v-text-field>
                </div>
                <div class="booking-section">
                    <div class="booking-section-header mb-2">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-text</v-icon>
                        EMAIL CONTENT
                    </div>
                    <wysiwyg v-model="body" placeholder="Compose the invitee email..." />
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">Cancel</v-btn>
                <v-btn dark color="#3988AA" :loading="sending" :disabled="!subject || !body" @click="sendEmail()">
                    <v-icon small left>mdi-send</v-icon>
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import { brandFor } from "@/utils/brand"
import { currencyFor, balanceDueLabel, paymentDeadlinesHtml, securityDepositHtml } from "@/utils/paymentTerms"

export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        invitee: {
            type: Object,
            default: () => ({})
        },
        event: {
            type: Object,
            default: () => ({})
        },
        company_id: {
            type: Number,
            default: null
        }
    },
    data: () => ({
        subject: '',
        body: '',
        sending: false,
    }),
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.sending = false
                this.subject = this.buildDefaultSubject()
                this.body = this.buildDefaultBody()
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close')
        },
        buildDefaultSubject() {
            return `Login details for ${this.event?.name || ''}`
        },
        buildDefaultBody() {
            const invitee = this.invitee || {}
            const event = this.event || {}
            const companyName = event.company?.company_name || ''
            const advisorName = this.get_user?.name || ''
            const loginLink = `https://client.gogroupbooking.com/${companyName}`

            const termsUrl = brandFor(event.company, event.lead?.event_type).terms_url
            const termsHtml = termsUrl
                ? `<br><br>You can view our Terms and Conditions for this booking <a href="${termsUrl}">here</a>.`
                : ''
            const deadlinesHtml = paymentDeadlinesHtml(event) + securityDepositHtml(event.company)
            const currency = currencyFor(event.company)
            const balanceDue = balanceDueLabel(event.company)

            // "Stag" → "Stag Party"; a type already named "... Party" (or just "Party") isn't doubled up
            const typeName = (event.lead?.event_type?.name || '').trim()
            const partyType = !typeName ? 'Party' : /party$/i.test(typeName) ? typeName : `${typeName} Party`
            const article = /^[aeiou]/i.test(partyType) ? 'an' : 'a'

            return `
                <div style="text-align:center;font-size:28px;color:black;"><b>Hello ${invitee.name || ''},</b></div>
                <br>
                <p style="text-align:left;color:black;font-size:16px;">
                    ${advisorName} here.
                    <br><br>
                    <b>You've been invited to ${article} ${partyType}!</b>
                    <br>
                    Just follow the 3 steps below to secure your place. If you are unsure of anything, please do not hesitate to ask.
                    <br><br>
                    <b>Step 1: Log in to your account</b>
                    <br>
                    Go to <a href="${loginLink}">${loginLink}</a> and log in with:
                    <br>
                    Username: ${invitee.email || ''}
                    <br>
                    Password: ${event.password || ''}
                    <br><br>
                    <b>Step 2: Check the itinerary</b>
                    <br>
                    Open the itinerary page to see everything we have arranged for the weekend, and check the times and dates are correct.
                    <br><br>
                    <b>Step 3: Pay to secure your place</b>
                    <br>
                    Deposit: You have 3 weeks from today to secure your place with a ${currency}50 deposit.
                    <br>
                    Full Payment: The remaining balance is due ${balanceDue} before the event. The pay date is always the Friday ${balanceDue} before the event date, so everything is settled before you arrive at your destination.
                    <br>
                    You can pay both from your account, and see your payments there at any time.${termsHtml}${deadlinesHtml}
                    <br><br>
                    We look forward to seeing you there!
                </p>
            `
        },
        async sendEmail() {
            this.sending = true
            try {
                const payload = {
                    company_id: this.company_id,
                    invitee: this.invitee,
                    event: this.event,
                    subject: this.subject,
                    body: this.body,
                }
                const result = await (await this.$axios.post('admin/events/send_invitee_email', payload)).data
                if (result.response == true) {
                    this.$toast.success(result.message)
                    this.closeDialog()
                } else {
                    this.$toast.error('Failed to send invitee email')
                }
            } catch (error) {
                this.$toast.error('Failed to send invitee email')
            } finally {
                this.sending = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}
</style>
